import fieldTypes from '@/lib/field-types';
import { DateTime } from 'luxon';
import { BANK_DETAILS, PERSONAL_DETAILS, SUPERANNUATION, TAX_DETAILS, WORK_RIGHTS_UK_FIELDS } from '@/core/field-names';
import { shouldEnableLegalName } from '@/core/feature-flags';

const isLegalNameEnabled = shouldEnableLegalName();

export function getValueKeyName(fieldType) {
  return fieldTypes[fieldType];
}

function normalizeValue(field = {}) {
  const type = getValueKeyName(field.type);

  if (!type) {
    return field.value;
  }
  return field[type];
}

function isFileValueEmpty(fileValueObj) {
  return Object.keys(fileValueObj || []).length === 0;
}

function getFieldIndexKey(field) {
  return `${field.name}-${field.id}`;
}

export default function documentsShape({
  documents = [],
  documentsData = [],
  documentTypes = [],
  fileUploads = [], // TODO: FE fileUpload hack to provide full info to HR automated
  defaultData = [],
  company = {},
  employee = {},
}) {
  const defaultDataFieldsIndexed = {};
  defaultData.reduce((reduced, { documentName, fields }) => {
    reduced[documentName] = fields;

    fields.forEach((ddf) => {
      defaultDataFieldsIndexed[ddf.name] = ddf;
    });

    return reduced;
  }, {});

  const fieldsIndexed = {};
  const documentsDataIndexed = documentsData.reduce((reduced, document) => {
    reduced[document.name] = document;

    // create field indexes
    (document.fields || []).forEach((field) => {
      const fieldIndexKey = getFieldIndexKey(field);
      fieldsIndexed[fieldIndexKey] = field;
    });

    return reduced;
  }, {});

  const docs = documents.map((document) => {
    document.id = documentsDataIndexed[document.name]
      ? documentsDataIndexed[document.name].id
      : null;

    if (!document.fields) {
      document.fields = [];
    } else {
      // TODO: Make it right! value normalization for now
      document.fields = document.fields.map((field) => {
        const fieldIndexKey = getFieldIndexKey(field);
        Object.assign(field, fieldsIndexed[fieldIndexKey]);

        // default value
        field.defaultValue =
          normalizeValue(defaultDataFieldsIndexed[field.name]) || null;

        field.defaultValueComputed = false;

        //
        field.value = normalizeValue(field) || field.defaultValue;

        // TODO: FE fileUpload hack to provide full info to HR automated
        if (field.type === 'FILE_UPLOAD') {
          const systemDocumentsWithNoExpiry = [
            SUPERANNUATION.SELF_MANAGED_FORM,
            TAX_DETAILS.P45_UPLOAD,
            WORK_RIGHTS_UK_FIELDS.BIRTH_CERTIFICATE,
            WORK_RIGHTS_UK_FIELDS.SUPPORTING_DOCUMENTS,
          ];

          const isSystemDocWithNoExpiry = systemDocumentsWithNoExpiry.includes(
            field.name
          );

          const docType = documentTypes.find(
            (doc) => doc.id === (field.fileValue?.refId || field.id)
          );

          field.fileValue = {
            ...field.fileValue,
            description: docType?.description,
            hasExpiry: docType?.hasExpiry,
          };
          if (
            (docType?.hasExpiry && !isSystemDocWithNoExpiry) ||
            field.name === WORK_RIGHTS_UK_FIELDS.SUPPORTING_DOCUMENTS
          ) {
            field.requiredValues = {
              ...field.requiredValues,
              expiredAt: false,
            };

            field.hasExpiry = true;
          }

          if (field.name === WORK_RIGHTS_UK_FIELDS.SUPPORTING_DOCUMENTS) {
            field.requiredValues = {
              expiredAt: true,
            };
          }

          if (!isSystemDocWithNoExpiry) {
            field.requiredValues = {
              ...field.requiredValues,
              hasFile: false,
            };
          }

          const formattedFileValue = fileUploads.find(
            (file) => file.id === field.id
          );

          if (formattedFileValue) {
            if (!isSystemDocWithNoExpiry) {
              field.requiredValues.hasFile = true;
              if (docType?.hasExpiry) {
                field.requiredValues.expiredAt = !!field.fileValue?.expiredAt;
              }
            }

            let formattedDate = null;

            if (field.fileValue?.expiredAt) {
              const dateTime = DateTime.fromISO(field.fileValue?.expiredAt);
              formattedDate = dateTime.toFormat('yyyy-MM-dd');
            }

            formattedFileValue.name =
              formattedFileValue.name || field.fileValue?.name;
            formattedFileValue.expiredAt = formattedDate;
            formattedFileValue.sizeInBytes =
              field.fileValue?.sizeInBytes || formattedFileValue.sizeInBytes;

            field.fileValue = {
              ...field.fileValue,
              name: formattedFileValue?.name,
              expiredAt:
                field.fileValue?.expiredAt || formattedFileValue?.expiredAt,
              sizeInBytes: formattedFileValue?.sizeInBytes,
            };
          } else {
            if (field.fileValue?.refId) {
              field.fileValue = { refId: field.fileValue?.refId };
            } else {
              field.fileValue = {};
            }
          }

          const birthCertificate = document.fields.find(
            (field) => field.name === WORK_RIGHTS_UK_FIELDS.BIRTH_CERTIFICATE
          );

          if (
            birthCertificate &&
            !isFileValueEmpty(birthCertificate.fileValue) &&
            field.name === WORK_RIGHTS_UK_FIELDS.PASSPORT &&
            isFileValueEmpty(field.fileValue)
          ) {
            field.requiredValues.expiredAt = true;
          }
        }

        // children field
        // TODO: move this solution to a recursive one
        if (field.children) {
          field.children = field.children.map((child) => {
            const fieldIndexKey = getFieldIndexKey(child);
            Object.assign(child, fieldsIndexed[fieldIndexKey]);
            child.value = normalizeValue(child);
            child.valueObj = normalizeValue(child);
            return child;
          });
        }

        switch (field.name) {
          case PERSONAL_DETAILS.EMAIL:
            field.validation.disabled = true;
            field.value = employee.email;
            break;

          case PERSONAL_DETAILS.MOBILE: {
            field.value = employee.mobile;
            field.mobileValue = employee.mobile;

            if (!isLegalNameEnabled) {
              field.validation.disabled = true;
            }

            break;
          }

          case PERSONAL_DETAILS.FIRST_NAME: {
            if (isLegalNameEnabled && !field.stringValue) {
              field.value = employee.firstName;
            }

            break;
          }

          case PERSONAL_DETAILS.LAST_NAME: {
            if (isLegalNameEnabled && !field.stringValue) {
              field.value = employee.lastName;
            }

            break;
          }

          case BANK_DETAILS.BSB:
            // format the string into 000 000 format when it is longer than 3 letter and does not have space
            if (!field.value?.includes(' ') && field.value?.length >= 3) {
              field.value =
                field.value.substring(0, 3) +
                ' ' +
                field.value.substring(3, field.value.length);
            }
            break;
        }

        field.appliedRules = {};

        return field;
      });
    }

    // TODO: flatfy sections on the documents, make it recursive
    // nested documents
    if (document.sections && document.sections.length > 0) {
      document.sections.forEach((section, k) => {
        document.fields = document.fields.concat(k);

        if (section.fields) {
          // TODO: verify why concat is NOT working
          section.fields.forEach((field) => {
            const fieldIndexKey = getFieldIndexKey(field);
            Object.assign(field, fieldsIndexed[fieldIndexKey]);
            field.value = normalizeValue(field);

            if (field.children) {
              field.children = field.children.map((child) => {
                const fieldIndexKey = getFieldIndexKey(child);
                Object.assign(child, fieldsIndexed[fieldIndexKey]);
                child.value = normalizeValue(child);
                field.appliedRules = {};
                return child;
              });
            }
            document.fields.push(field);
          });

          document.fields = document.fields.concat(section.fields);
        }
      });
    }

    document.slug = `${company.name.toLowerCase()}-${document.title
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')}`;

    return document;
  });

  return docs;
}
